import type { PortfolioExAnteMetric } from "@eu.mdotm.api/sphere";
import { Card } from "$root/components/EvolvedPrint/components/Card";
import { commonReportColumns } from "$root/components/EvolvedPrint/components/table/common-report-columns";
import { TinyTableDataCell, TinyTableHeadCell } from "$root/components/EvolvedPrint/components/table/tiny-table";
import type { PrintableProps } from "$root/components/EvolvedPrint/configuration/types";
import type { ReportTemplateVariant } from "$root/functional-areas/reports/report-latest";
import { useLocaleFormatters } from "$root/localization/hooks";
import { type TableColumn, BaseTable } from "@mdotm/shadcn/legacy/components";
import { useReportTranslation } from "../translation";
import type { UnitOfMeasurement } from "$root/functional-areas/metrics";

type CustomPortfolioExAnteMetric = PortfolioExAnteMetric & {
	unitOfMeasurement: UnitOfMeasurement;
	decimalPlaces?: number;
};
const ExAnteMetrics = ({
	benchmark: showBechmark,
	variant,
	list,
	proposalDate,
}: PrintableProps<
	{ benchmark: boolean; variant: ReportTemplateVariant; proposalDate: string | undefined },
	CustomPortfolioExAnteMetric
>): React.JSX.Element => {
	const { formatDate } = useLocaleFormatters();
	const { t } = useReportTranslation();

	const columns = [
		{
			name: "name",
			width: variant === "current" ? 330 : 200,
			header: (props) => (
				<TinyTableHeadCell {...props}>{t("REPORT_BUILDER.EX_ANTE_PORTFOLIO_METRICS.TABLE.METRIC")}</TinyTableHeadCell>
			),
			content: ({ type, metadata, metricParameters }, props) => (
				<TinyTableDataCell {...props}>
					{metadata?.classificationName && metricParameters?.classificationUuid
						? `Avg. ${metadata?.classificationName}`
						: type
							? t(`REPORT_BUILDER.EX_ANTE_PORTFOLIO_METRICS.METRICS.${type}`)
							: ""}
				</TinyTableDataCell>
			),
		},
		{
			...commonReportColumns.float<CustomPortfolioExAnteMetric>(
				"current",
				variant === "current"
					? t("REPORT_BUILDER.EX_ANTE_PORTFOLIO_METRICS.TABLE.CURRENT")
					: t("REPORT_BUILDER.EX_ANTE_PORTFOLIO_METRICS.TABLE.COMPOSITION_AT", {
							date: formatDate(proposalDate, { omitYear: true }),
						}),
				{ decimalPlaces: (row) => row.decimalPlaces, suffix: (row) => row.unitOfMeasurement },
			),
			name: "current",
			width: 124,
			headerCellClassName: "whitespace-pre-line",
		},
		{
			...commonReportColumns.float<CustomPortfolioExAnteMetric>(
				"benchmark",
				t("REPORT_BUILDER.EX_ANTE_PORTFOLIO_METRICS.TABLE.BENCHMARK"),
				{ decimalPlaces: (row) => row.decimalPlaces, suffix: (row) => row.unitOfMeasurement },
			),
			name: "benchmark",
			width: 124,
			hidden: !showBechmark,
		},
		{
			...commonReportColumns.float<CustomPortfolioExAnteMetric>(
				"proposal",
				t("REPORT_BUILDER.EX_ANTE_PORTFOLIO_METRICS.TABLE.PROPOSAL"),
				{ decimalPlaces: (row) => row.decimalPlaces, suffix: (row) => row.unitOfMeasurement },
			),
			name: "proposal",
			width: 124,
			hidden: variant === "current",
		},
		{
			name: "empty",
			minWidth: 0,
			header: (props) => <TinyTableHeadCell {...props}>{null}</TinyTableHeadCell>,
			content: (_, props) => <TinyTableDataCell {...props}>{null}</TinyTableDataCell>,
		},
	] satisfies TableColumn<CustomPortfolioExAnteMetric>[];

	return (
		<Card title={t("REPORT_BUILDER.EX_ANTE_PORTFOLIO_METRICS.TITLE")}>
			<BaseTable disableVirtualScroll palette="uniform" columns={columns} rows={list} noDataText="No metrics" />
		</Card>
	);
};

export default ExAnteMetrics;
